import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import {
  CircleCheckIcon,
  ExternalLinkIcon,
  PrinterIcon,
  ReceiptIcon,
  UserPlusIcon,
} from 'lucide-react'

import { Button } from '@ems/ui/components/button'
import { StatusBadge } from '@ems/ui/patterns/status-badge'

import { applicationTone, type Application } from '#admin/features/exhibitor/api.ts'
import type { Stand } from '#admin/features/exhibitor/onboarding/draft.ts'
import { badgeOf, printBadges } from '#admin/features/onsite/badge.ts'
import { label } from '#admin/features/registrations/api.ts'
import { registrationTicketsQuery } from '#admin/features/registrations/queries.ts'

/**
 * What the desk did, and what to do next: print the badges for the people at the desk, open the
 * application or an invoice, or start the next exhibitor.
 */
export function SuccessCard({
  application,
  stands,
  serviceInvoiceId,
  registrationId,
  badgeTypeName,
  eventName,
  onReset,
}: {
  application: Application
  stands: readonly Stand[]
  serviceInvoiceId: string | null
  registrationId: string | null
  badgeTypeName: string | null
  eventName: string
  onReset: () => void
}) {
  const tickets = useQuery({
    ...registrationTicketsQuery(registrationId ?? ''),
    enabled: registrationId !== null,
  })
  const printable = (tickets.data?.items ?? []).filter((ticket) => ticket.status === 'active')
  const print = () =>
    printBadges(
      printable.map((ticket) => badgeOf({ ...ticket, ticketTypeName: badgeTypeName }, eventName)),
    )

  return (
    <section
      aria-labelledby="desk-done"
      className="bg-card mx-auto flex w-full max-w-2xl flex-col gap-6 rounded-lg border border-(--border) p-6 md:p-8"
    >
      <div className="flex flex-col items-center gap-3 text-center">
        <span className="flex size-14 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400">
          <CircleCheckIcon aria-hidden="true" className="size-8" />
        </span>
        <h2 id="desk-done" className="text-xl font-semibold">
          {`${application.companyName} is registered`}
        </h2>
        <StatusBadge tone={applicationTone(application.status)}>
          {label(application.status)}
        </StatusBadge>
      </div>

      <dl className="grid gap-4 rounded-lg border border-(--border) p-4 text-sm sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <dt className="text-muted-foreground text-xs">Reference</dt>
          <dd className="font-mono text-base font-semibold">{application.code ?? '—'}</dd>
        </div>
        <div className="flex flex-col gap-1">
          <dt className="text-muted-foreground text-xs">Stands</dt>
          <dd className="font-medium">
            {stands
              .map((stand) => (stand.hall === null ? stand.code : `${stand.code} (${stand.hall})`))
              .join(', ')}
          </dd>
        </div>
        <div className="flex flex-col gap-1">
          <dt className="text-muted-foreground text-xs">Contact</dt>
          <dd>{`${application.contactName} · ${application.contactEmail}`}</dd>
        </div>
        <div className="flex flex-col gap-1">
          <dt className="text-muted-foreground text-xs">Badges</dt>
          <dd>
            {registrationId === null
              ? 'None issued'
              : tickets.isPending
                ? 'Loading…'
                : `${printable.length} issued`}
          </dd>
        </div>
      </dl>

      <div className="flex flex-wrap justify-center gap-2">
        {registrationId === null ? null : (
          <Button disabled={printable.length === 0} onClick={print}>
            <PrinterIcon />
            {`Print all badges (${printable.length})`}
          </Button>
        )}
        <Button variant="outline" asChild>
          <Link
            to="/exhibition/exhibitors/$applicationId"
            params={{ applicationId: application.id }}
          >
            <ExternalLinkIcon />
            View application
          </Link>
        </Button>
        {application.invoiceId === null ? null : (
          <InvoiceLink invoiceId={application.invoiceId} text="View stand invoice" />
        )}
        {serviceInvoiceId === null ? null : (
          <InvoiceLink invoiceId={serviceInvoiceId} text="View services invoice" />
        )}
        <Button variant="ghost" onClick={onReset}>
          <UserPlusIcon />
          Register another
        </Button>
      </div>
    </section>
  )
}

function InvoiceLink({ invoiceId, text }: { invoiceId: string; text: string }) {
  return (
    <Button variant="outline" asChild>
      <Link to="/billing/invoices/$invoiceId" params={{ invoiceId }}>
        <ReceiptIcon />
        {text}
      </Link>
    </Button>
  )
}
